import { Button } from "@egaranti/components";
import { Input } from "@egaranti/components";
import { Label } from "@egaranti/components";
import { Textarea } from "@egaranti/components";
import { useToast } from "@egaranti/components";
import { Switch } from "@egaranti/components";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@egaranti/components";
import { zodResolver } from "@hookform/resolvers/zod";

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import CampaignService from "@/services/campaignService";

import FormFieldBuilder from "@/components/productActivation/formFieldBuilder";
import { Breadcrumb, BreadcrumbItem } from "@/components/shared/breadcrumbs";

import { CopyIcon } from "lucide-react";
import * as z from "zod";

import { useCampaignFormFields } from "@/hooks/useCampaignFormFields";
import { getMerchantId } from "@/lib/authStorage";
import { formatDateTime, parseDateTime } from "@/lib/dateUtils";

import CheckRole from "@/components/shared/checkRole";

const formFieldSchema = z.object({
  id: z.string().optional(),
  label: z.string().optional(),
  type: z.string().optional(),
  required: z.boolean().optional(),
  options: z.array(z.string()).optional(),
  order: z.number().optional(),
});

const CampaignEditPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [validationLoading, setValidationLoading] = useState(false);
  const [campaign, setCampaign] = useState(null);

  const formSchema = z.object({
    name: z.string().min(1, { message: t("common.validateMsg.requiredField") }),
    description: z.string().optional(),
    startDate: z
      .string()
      .min(1, { message: t("common.validateMsg.requiredField") }),
    endDate: z
      .string()
      .min(1, { message: t("common.validateMsg.requiredField") }),
    extendedWarrantyDurationMonths: z
      .number({ required_error: t("common.validateMsg.requiredField") })
      .min(1, { message: t("common.validateMsg.requiredField") }),
    formFields: z.array(formFieldSchema).optional(),
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      startDate: "",
      endDate: "",
      extendedWarrantyDurationMonths: 12,
      formFields: [],
    },
  });

  const { fields, addFormField, removeField, updateFieldOrder } =
    useCampaignFormFields(form);

  useEffect(() => {
    fetchCampaign();
  }, [id]);

  const fetchCampaign = async () => {
    try {
      setLoading(true);
      const response = await CampaignService.getCampaign(id);
      const campaignData = response?.data;
      setCampaign(campaignData);

      // Transform backend form fields to frontend format
      let transformedFormFields = [];
      if (campaignData?.formFields && Array.isArray(campaignData.formFields)) {
        transformedFormFields = campaignData.formFields
          .map((f, index) => ({
            id: `field_${f.displayOrder !== undefined ? f.displayOrder : index}`,
            label: f.question || "",
            type: f.type ? f.type.toLowerCase() : "text",
            required: f.required || false,
            options: Array.isArray(f.options)
              ? f.options.filter((opt) => opt !== "")
              : [],
            order: f.displayOrder !== undefined ? f.displayOrder : index,
          }))
          .sort((a, b) => a.order - b.order);
      }

      form.reset({
        name: campaignData?.name || "",
        description: campaignData?.description || "",
        startDate: parseDateTime(campaignData?.startDate),
        endDate: parseDateTime(campaignData?.endDate),
        extendedWarrantyDurationMonths:
          campaignData?.extendedWarrantyDurationMonths || 12,
        formFields: transformedFormFields,
      });
    } catch (error) {
      toast({
        title: t("common.error"),
        description: t("campaignsPage.fetchError"),
        variant: "error",
      });
      navigate("/campaigns");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      // Transform form fields to backend format
      const formFields = (data.formFields || []).map((field, index) => ({
        question: field.label,
        type: field.type.toUpperCase(),
        required: field.required || false,
        options: field.options || [],
        displayOrder: field.order ?? index,
      }));

      const payload = {
        name: data.name,
        description: data.description || "",
        extendedWarrantyDurationMonths: data.extendedWarrantyDurationMonths,
        startDate: formatDateTime(data.startDate),
        endDate: formatDateTime(data.endDate),
        formFields,
      };

      await CampaignService.updateCampaign(id, payload);

      toast({
        title: t("common.success"),
        description: t("campaignsPage.updateSuccess"),
        variant: "success",
      });

      navigate("/campaigns");
    } catch (error) {
      const errorMessage =
        error?.response?.data?.errors?.[0]?.message ||
        error?.response?.data?.message ||
        t("campaignsPage.updateError");
      toast({
        title: t("common.error"),
        description: errorMessage,
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCampaignLink = () => {
    const merchantId = getMerchantId();
    const campaignLink = `https://embed.egaranti.com/campaigns/${id}/?company=${merchantId}`;
    navigator.clipboard.writeText(campaignLink);

    toast({
      title: t("common.success"),
      description: t("campaignsPage.campaignLinkCopied"),
      variant: "success",
    });
  };

  const handleValidationToggle = async (checked) => {
    try {
      setValidationLoading(true);
      if (checked) {
        await CampaignService.validateCampaign(id);
        toast({
          title: t("common.success"),
          description: t("campaignsPage.validateSuccess"),
          variant: "success",
        });
      } else {
        await CampaignService.invalidateCampaign(id);
        toast({
          title: t("common.success"),
          description: t("campaignsPage.invalidateSuccess"),
          variant: "success",
        });
      }
      setCampaign((prev) => ({ ...prev, isValid: checked }));
    } catch (error) {
      const errorMessage =
        error?.response?.data?.errors?.[0]?.message ||
        error?.response?.data?.message ||
        t("campaignsPage.validationError");
      toast({
        title: t("common.error"),
        description: errorMessage,
        variant: "error",
      });
    } finally {
      setValidationLoading(false);
    }
  };

  if (loading && !campaign) {
    return (
      <main className="min-h-screen bg-[#F9FAFC] p-6">
        <div className="mx-auto max-w-4xl">
          <div className="formBox">
            <p className="text-center text-[#677389]">{t("common.loading")}</p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#F9FAFC]">
      <Breadcrumb>
        <BreadcrumbItem link="/campaigns">
          {t("campaignsPage.title")}
        </BreadcrumbItem>
        <BreadcrumbItem active>{campaign?.name}</BreadcrumbItem>
      </Breadcrumb>

      <div className="mx-auto max-w-4xl py-6">
        <div>
          <h1 className="formTitle">{t("campaignsPage.editCampaign")}</h1>
          <p className="formDescription">
            {t("campaignsPage.editCampaignDesc")}
          </p>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="mt-6 space-y-6"
          >
            {/* Basic Information */}
            <div className="formBox">
              <h2 className="mb-4 text-lg font-semibold text-[#101828]">
                {t("campaignsPage.campaignInfo")}
              </h2>

              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <Label>{t("campaignsPage.campaignName")} *</Label>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <Label>{t("campaignsPage.campaignDescription")}</Label>
                      <FormControl>
                        <Textarea rows={4} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="startDate"
                    render={({ field }) => (
                      <FormItem>
                        <Label>{t("campaignsPage.startDate")} *</Label>
                        <FormControl>
                          <Input type="datetime-local" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="endDate"
                    render={({ field }) => (
                      <FormItem>
                        <Label>{t("campaignsPage.endDate")} *</Label>
                        <FormControl>
                          <Input type="datetime-local" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="extendedWarrantyDurationMonths"
                  render={({ field }) => (
                    <FormItem>
                      <Label>{t("campaignsPage.warrantyExtension")} *</Label>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Campaign Status */}
            <div className="formBox">
              <h2 className="mb-4 text-lg font-semibold text-[#101828]">
                {t("campaignsPage.campaignStatus")}
              </h2>

              <div className="flex items-center justify-between rounded-lg border p-4">
                <div>
                  <p className="font-medium text-[#101828]">
                    {t("campaignsPage.isValid")}
                  </p>
                  <p className="text-sm text-[#677389]">
                    {campaign?.isValid
                      ? t("campaignsPage.campaignActive")
                      : t("campaignsPage.campaignInactive")}
                  </p>
                </div>
                <CheckRole section="CAMPAIGNS" action="EDIT">
                  <Switch
                    checked={campaign?.isValid || false}
                    onCheckedChange={handleValidationToggle}
                    disabled={validationLoading}
                  />
                </CheckRole>
              </div>
            </div>

            {/* Campaign Link */}
            <div className="formBox">
              <h2 className="mb-4 text-lg font-semibold text-[#101828]">
                {t("campaignsPage.campaignLink")}
              </h2>

              <div className="space-y-4">
                <div className="rounded-lg bg-green-50 p-4">
                  <p className="text-sm text-[#677389]">
                    {t("campaignsPage.campaignLinkDesc")}
                  </p>
                  <div className="mt-3 flex items-center gap-2 rounded-md bg-white p-3">
                    <code className="flex-1 break-all text-sm text-[#677389]">
                      {`https://embed.egaranti.com/campaigns/${id}/?company=${getMerchantId()}`}
                    </code>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleCopyCampaignLink}
                    >
                      <CopyIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Fields */}
            <div className="formBox">
              <FormFieldBuilder
                fields={fields}
                addFormField={addFormField}
                updateFieldOrder={updateFieldOrder}
                removeField={removeField}
                form={form}
              />
            </div>

            {/* Form Actions */}
            <CheckRole section="CAMPAIGNS" action="EDIT">
              <div className="flex justify-end gap-4">
                <Button
                  type="button"
                  variant="secondaryGray"
                  onClick={() => navigate("/campaigns")}
                  disabled={loading}
                >
                  {t("common.cancel")}
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? t("common.saving") : t("common.save")}
                </Button>
              </div>
            </CheckRole>
          </form>
        </Form>
      </div>
    </main>
  );
};

export default CampaignEditPage;
